import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, Edit, Trash2, Package, MapPin, Clock, CheckCircle, User, Phone, Calendar, DollarSign, Download } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import * as XLSX from 'xlsx';

const Deliveries = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [emirateFilter, setEmirateFilter] = useState('all');
  const [orders, setOrders] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const { showError, showSuccess } = useToast();

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      setLoading(true);
      
      // Get client info from localStorage
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (tokenData) {
        const userData = JSON.parse(tokenData);
        const clientData = {
          Id: userData.ClientId,
          Token: userData.Token
        };
        setSelectedClient(clientData);

        // Load today's orders
        const ordersResponse = await MainService.TodaysOrdersInPortal(clientData.Id, clientData.Token);
        setOrders(ordersResponse || []);

        // Load emirates
        const emiratesResponse = await MainService.EmiratesInDropDown();
        setEmirates(emiratesResponse || []);

        // Load status options
        const statusResponse = await MainService.StatusInDropDown();
        setStatusOptions(statusResponse || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load delivery data');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const exportData = filteredOrders.map((order, index) => ({
        '#': index + 1,
        'Voucher No': order.VoucherNo || 'N/A',
        'Bill No': order.ClientBillNo || 'N/A',
        'Customer Name': order.Customer?.Name || 'N/A',
        'Mobile No': order.Customer?.MobileNo1 || 'N/A',
        'Status': order.Status?.Name || 'N/A',
        'Address': order.CustomerAddress?.Address || 'N/A',
        'Emirate': order.CustomerAddress?.Emirate?.Name || 'N/A',
        'Pickup Date': formatDate(order.PickupDate),
        'Delivery Date': formatDate(order.DeliveryDate),
        'Amount': order.CustomerAmount || 0
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Today Orders');
      
      const fileName = `deliveries_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showSuccess('Deliveries exported to Excel successfully!');
    } catch (error) {
      console.error('Export error:', error);
      showError('Failed to export deliveries to Excel');
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    
    // Use backend color if available
    if (status.Color) {
      // Return a special marker for inline styles
      return 'custom-color';
    }
    
    // Fallback to default colors if no backend color
    const statusName = status.Name || status;
    switch (statusName.toLowerCase()) {
      case 'in transit':
      case 'picked up':
        return 'text-primary-600 bg-primary-100';
      case 'delivered':
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'out for delivery':
        return 'text-blue-600 bg-blue-100';
      case 'processing':
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'delayed':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusStyle = (status) => {
    if (!status || !status.Color) return {};
    
    return {
      color: status.Color,
      backgroundColor: `${status.Color}20`, // 20% opacity
      border: `1px solid ${status.Color}40` // 40% opacity border
    };
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.VoucherNo && order.VoucherNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.ClientBillNo && order.ClientBillNo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.Customer && order.Customer.Name && order.Customer.Name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.CustomerAddress && order.CustomerAddress.Address && order.CustomerAddress.Address.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
      (order.Status && (order.Status.Name === statusFilter || order.Status.Keyword === statusFilter));
    
    const matchesEmirate = emirateFilter === 'all' || 
      (order.CustomerAddress && order.CustomerAddress.Emirate && order.CustomerAddress.Emirate.Name === emirateFilter);
    
    return matchesSearch && matchesStatus && matchesEmirate;
  });

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Deliveries</h1>
          <p className="text-gray-600">Manage and track all your deliveries</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by voucher, bill no, customer, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-40 px-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                >
                  <option value="all">All Status</option>
                  {statusOptions.map((status) => (
                    <option key={status.Id || status.id} value={status.Name || status.name}>
                      {status.Name || status.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-gray-500" />
                <select
                  value={emirateFilter}
                  onChange={(e) => setEmirateFilter(e.target.value)}
                  className="w-40 px-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                >
                  <option value="all">All Emirates</option>
                  {emirates.map(emirate => (
                    <option key={emirate.Id} value={emirate.Name}>
                      {emirate.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header with Summary */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Today's Orders</h3>
                <p className="text-sm text-gray-600">Detailed view of all orders with comprehensive information</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Total Orders: {filteredOrders.length}</span>
                </div>
                <button 
                  onClick={exportToExcel}
                  disabled={filteredOrders.length === 0}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200 shadow-sm"
                >
                  <Download className="w-4 h-4" />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center p-10 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
                Loading today's orders...
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center p-10 text-gray-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
                <p className="text-sm">No orders for today or try adjusting your filters.</p>
              </div>
            ) : (
              <table className="w-full rounded-2xl m-4">
              <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="text-center py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">VOUCHER NO</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CLIENT BILL</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">BATCH NO</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY DATE</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">PRODUCT PRICE</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY CHARGE</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">TAX</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">COLLECTION AMOUNT</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">WALLET AMOUNT</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">CUSTOMER</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">DELIVERY ADDRESS</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Emirate</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                    <th className="text-left py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, index) => (
                  <tr key={order.Id || index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                    <td className="py-4 px-4 text-center text-sm text-gray-500 font-medium">
                      {index + 1}
                    </td>
                    <td className="py-4 px-4 text-sm font-medium text-gray-900">
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        {order.VoucherNo || 'N/A'}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">{order.ClientBillNo || 'N/A'}</td>
                    <td className="py-4 px-4 text-sm text-gray-700">{order.BatchNo || 'N/A'}</td>
                    <td className="py-4 px-4">
                      <span 
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.Status) === 'custom-color' ? '' : getStatusColor(order.Status)}`}
                        style={getStatusColor(order.Status) === 'custom-color' ? getStatusStyle(order.Status) : {}}
                      >
                        {order.Status?.Name || 'N/A'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {order.DeliveryDate ? new Date(order.DeliveryDate).toLocaleDateString('en-GB') : 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-sm font-semibold text-gray-900">
                      {formatCurrency(order.ProductPrice)}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {formatCurrency(order.DeliveryCharge)}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {formatCurrency(order.TAX)}
                    </td>
                    <td className="py-4 px-4 text-sm font-bold text-green-600">
                      {formatCurrency(order.CustomerAmount)}
                    </td>
                    <td className="py-4 px-4 text-sm font-bold text-blue-600">
                      {formatCurrency(order.ClientAmount)}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {order.Customer?.Name || 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {order.CustomerAddress?.Address || 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {order.CustomerAddress?.Emirate?.Name || 'N/A'}
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700">
                      {order.CustomerAddress?.Location?.Name || 'N/A'}
                    </td>
                  
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => navigate(`/order-details/${order.Id}`)}
                          className="p-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors duration-200"
                          title="View Order Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-200">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deliveries; 